import { isObject, TypeGuard } from '@principle-theorem/shared';
import { isFunction, isString } from 'lodash';
import { BigQueryTable } from './big-query-tables';
import {
  ICanBeChartedProperty,
  ICanDoAllProperty,
  ICanQueryByTimestampProperty,
  IMeasureRef,
  isCanDoAllProperty,
} from './measure-properties';
import { IQueryScopeConfig } from './query-scope-config';
import {
  GroupBy,
  IReportingQuery,
  ITableJoin,
  IUnnestQuery,
  SortOrder,
} from './querying';

export interface IQueryFactory {
  get(): IReportingQuery;
  attributes(measures: string[]): IQueryFactory;
  unnest(unnest: IUnnestQuery): IQueryFactory;
  mergeJoins(joins?: ITableJoin[]): IQueryFactory;
  override(query: Partial<IReportingQuery>): IQueryFactory;
  merge(queries: Partial<IReportingQuery>[]): IQueryFactory;
  latestEvent(
    table: BigQueryTable,
    groupBy: GroupBy,
    orderBy?: string,
    sortOrder?: SortOrder
  ): IQueryFactory;
  filterSoftDeleted(
    dimension: IBaseFactMeasures | IBaseMeasures,
    measurePath?: string
  ): IQueryFactory;
  filter(filter: string): IQueryFactory;
}

export interface IBaseMeasures<T = unknown> {
  table: BigQueryTable;
  id: string;
  scopes: IQueryScopeConfig<T>[];
  query: Partial<IReportingQuery>;
  timestamp: ICanQueryByTimestampProperty;
  count: ICanDoAllProperty;

  getResourceName(): string;
  buildQuery(): IQueryFactory;
  scopeBy(chartedProperty: ICanBeChartedProperty): IBaseMeasures<T>;
  measureRef(path: string, isUnnestProperty?: boolean): IMeasureRef;
}

export const isBaseMeasures = TypeGuard.interface<IBaseMeasures>({
  table: TypeGuard.enumValue(BigQueryTable),
  id: isString,
  scopes: TypeGuard.arrayOf(isObject),
  query: isObject,
  timestamp: isObject,
  count: isCanDoAllProperty,
  getResourceName: isFunction,
  buildQuery: isFunction,
  scopeBy: isFunction,
  measureRef: isFunction,
});

export interface IBaseFactMeasures<T = unknown> extends IBaseMeasures<T> {
  timestampDay: ICanDoAllProperty;
}

export const isBaseFactMeasures = TypeGuard.interface<IBaseFactMeasures>({
  table: TypeGuard.enumValue(BigQueryTable),
  id: isString,
  scopes: TypeGuard.arrayOf(isObject),
  query: isObject,
  timestamp: isObject,
  count: isCanDoAllProperty,
  getResourceName: isFunction,
  buildQuery: isFunction,
  scopeBy: isFunction,
  measureRef: isFunction,
  timestampDay: isCanDoAllProperty,
});
